import type { ReactElement } from 'react';
import styled from 'styled-components';
import { BusinessHours } from '@elfsight/widget-business-hours/widget';
import { useIsMobileBreakpoint } from '@elfsight/widget-utils';
import { BusinessInfo } from '../business-info';
import { BusinessLogoImage } from '../business-logo-image';
import {
  LOCATION_CARD_MIN_COLUMN_WIDTH,
  LOCATION_CARD_OFFSET_BETWEEN_COLUMNS
} from '../location-card-constants';
import { useLocationCardProps } from '../location-card-settings-context';
import { LocationPhotoMedia } from '../location-photo-media';
import { Content } from './content';

export function MultiColumnLayout({ maxColumns }: { maxColumns: number }) {
  const settings = useLocationCardProps();
  const isMobileBreakpoint = useIsMobileBreakpoint(639);
  const {
    background,
    contactsPhone,
    contactsEmail,
    contactsAddress,
    contactsInstagram,
    contactsWhatsApp,
    contactsWebsite,
    locationPhotoImage,
    locationPhotoDisplayMobile,
    locationPhotoPosition,
    locationPhotoMobilePosition,
    ctaButtons,
    businessHoursDisplay,
    footerComponent,
    businessLogo,
    businessLogoStyle,
    businessLogoSize,
    businessLogoColor,
    businessName,
    businessAbout,
    alignment,
    footerOffset,
    locationLabels
  } = settings;

  // +1 because schedule is always visible
  const regularBlocksCount =
    [
      !!contactsPhone,
      !!contactsEmail,
      !!contactsAddress,
      !!contactsWebsite,
      !!contactsWebsite,
      !!contactsInstagram,
      !!contactsWhatsApp
    ].filter(Boolean).length + 1;

  const businessInfoInHeader =
    [
      !!businessName,
      !!businessAbout,
      !!businessLogo,
      !!ctaButtons?.length,
      !!locationLabels?.length
    ].filter(Boolean).length <= 2 || regularBlocksCount <= 2;

  const oneColumnLayout = maxColumns <= 1 || alignment === 'center';

  const locationPhotoOnBottom =
    oneColumnLayout && locationPhotoMobilePosition === 'bottom';

  const locationPhotoOnTop =
    (!isMobileBreakpoint && locationPhotoPosition === 'center') ||
    (isMobileBreakpoint && locationPhotoMobilePosition === 'top');

  const locationPhotoEnabled =
    (!isMobileBreakpoint && locationPhotoImage) ||
    (isMobileBreakpoint && locationPhotoImage && locationPhotoDisplayMobile);

  const overlapLogoAvailable =
    businessLogo &&
    businessLogoStyle === 'overlap' &&
    locationPhotoEnabled &&
    locationPhotoOnTop;

  const contentMarginTop = locationPhotoOnTop && locationPhotoEnabled ? 40 : 0;
  const contentMarginBottom =
    locationPhotoOnBottom && locationPhotoEnabled ? 32 : 0;

  const renderWrapper = (children: ReactElement) => {
    return (
      <ContentContainer
        _marginTop={
          overlapLogoAvailable ? -businessLogoSize / 2 : contentMarginTop
        }
        _marginBottom={contentMarginBottom}
      >
        {overlapLogoAvailable && (
          <OverlapLogoContainer
            _alignment={alignment}
            _hasOffset={background.color === 'transparent'}
          >
            <BusinessLogoImage color={businessLogoColor} style="overlap" />
          </OverlapLogoContainer>
        )}

        {children}
      </ContentContainer>
    );
  };

  if (oneColumnLayout) {
    return renderWrapper(
      <Columns>
        {maxColumns > 1 &&
          locationPhotoImage &&
          locationPhotoPosition === 'left' && (
            <Column>
              <LocationPhotoMedia />
            </Column>
          )}

        <Column>
          <BusinessInfo withLogo={!overlapLogoAvailable} />
          <Content withSchedule />

          {footerComponent && (
            <FooterContainer _offset={footerOffset}>
              {footerComponent}
            </FooterContainer>
          )}
        </Column>

        {maxColumns > 1 &&
          locationPhotoImage &&
          locationPhotoPosition === 'right' && (
            <Column>
              <LocationPhotoMedia />
            </Column>
          )}
      </Columns>
    );
  }

  return renderWrapper(
    <Columns>
      {locationPhotoImage &&
        locationPhotoPosition === 'left' &&
        !isMobileBreakpoint && (
          <Column>
            <LocationPhotoMedia />
          </Column>
        )}
      <ContainerWithHeader _withHeader={businessInfoInHeader}>
        <BusinessInfo withLogo={!overlapLogoAvailable} />

        <BusinessContactsAndHoursContainer
          _oneColumn={regularBlocksCount < 3}
          _alignment={alignment}
        >
          <Column>
            <Content withSchedule={false} />
          </Column>
          {businessHoursDisplay === 'onPage' && (
            <Column>
              <BusinessHours mode="hours" {...settings} />
            </Column>
          )}
        </BusinessContactsAndHoursContainer>

        {footerComponent && (
          <FooterContainer _offset={footerOffset}>
            {footerComponent}
          </FooterContainer>
        )}
      </ContainerWithHeader>

      {locationPhotoImage &&
        locationPhotoPosition === 'right' &&
        !isMobileBreakpoint && (
          <Column>
            <LocationPhotoMedia />
          </Column>
        )}
    </Columns>
  );
}

const ContentContainer = styled.div<{
  _marginTop?: number;
  _marginBottom?: number;
}>`
  margin-top: ${({ _marginTop }) => _marginTop}px;
  margin-bottom: ${({ _marginBottom }) => _marginBottom}px;
`;

const OverlapLogoContainer = styled.div<{
  _alignment: 'left' | 'center' | 'right';
  _hasOffset?: boolean;
}>`
  box-sizing: border-box;
  padding: ${({ _hasOffset }) => (_hasOffset ? '0 40px' : 0)};
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: ${({ _alignment }) => _alignment};
`;

const FooterContainer = styled.div<{ _offset?: number }>`
  margin-top: ${({ _offset = 32 }) => _offset}px;
  min-width: 0;
  width: 100%;
`;

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: ${LOCATION_CARD_OFFSET_BETWEEN_COLUMNS}px;
`;

const Column = styled.div`
  display: block;
  width: fit-content;
  flex-grow: 1;
  min-width: ${LOCATION_CARD_MIN_COLUMN_WIDTH}px;
`;

const ContainerWithHeader = styled.div<{ _withHeader?: boolean }>`
  display: ${({ _withHeader }) => (_withHeader ? 'block' : 'flex')};
  flex-wrap: wrap;
  column-gap: ${LOCATION_CARD_OFFSET_BETWEEN_COLUMNS}px;
  width: 100%;
`;

const BusinessContactsAndHoursContainer = styled.div<{
  _oneColumn?: boolean;
  _alignment: 'left' | 'center' | 'right';
}>`
  display: flex;
  min-width: fit-content;
  flex-direction: ${({ _oneColumn }) => (_oneColumn ? 'column' : 'row')};
  justify-content: ${({ _alignment }) => _alignment};
  align-items: ${({ _alignment }) =>
    ({ left: 'flex-start', center: 'center', right: 'flex-end' })[_alignment]};
  gap: ${LOCATION_CARD_OFFSET_BETWEEN_COLUMNS}px;
  flex-grow: 1;
  flex-wrap: wrap;
`;
